import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react'
import classNames from 'classnames'
import { Fragment } from 'react'
import { isRouteErrorResponse, Link } from 'react-router'
import {
  BackgroundElements,
  PageLayoutWithoutHeader,
} from '~/components/Layout'
import { ROUTES } from '~/routes-typed'
import { focusRing } from '~/utils/styles'

interface ErrorPageProps {
  error: unknown
}

const strings = {
  unexpectedError: 'Unexpected error',
  homepage: 'Startseite',
}

export const ErrorDisplay = ({ children }: { children: React.ReactNode }) => {
  return (
    <div
      style={{ fontFamily: 'monospace' }}
      className='tracked-m border-lavender-500 text-lavender-700 flex max-w-full flex-col items-center gap-2 rounded-lg border-2 bg-white px-3 py-2 text-xs shadow-lg'
    >
      {children}
    </div>
  )
}

const ErrorTitle = (props: { status: number; statusText: string }) => {
  return (
    <div className='text-h6 text-grey-700 flex items-center tracking-wider'>
      <span className='border-grey-300 border-r pe-4'>{props.status}</span>
      <span className='ps-4'>{props.statusText}</span>
    </div>
  )
}

const Container = ({ children }: { children: React.ReactNode }) => {
  return (
    <PageLayoutWithoutHeader background={<BackgroundElements />}>
      <div className={'bg-grey-200 -inset-0 -z-20'} />
      <div className='flex min-h-screen max-w-full items-center justify-center'>
        <div className='text-grey-800 flex max-w-full flex-col items-center gap-5 p-4 md:p-8'>
          {children}
        </div>
      </div>
    </PageLayoutWithoutHeader>
  )
}

export const ErrorPage = ({ error }: ErrorPageProps) => {
  if (isRouteErrorResponse(error)) {
    const data = error.data

    const isDataString = data && typeof data === 'string'
    const dataKeys = data ? Object.keys(data) : []

    return (
      <Container>
        <ErrorTitle status={error.status} statusText={error.statusText} />
        <Link
          to={ROUTES.START.$path()}
          className={classNames(focusRing, 'underline')}
        >
          {strings.homepage}
        </Link>

        {isDataString ? (
          <ErrorDisplay>{data}</ErrorDisplay>
        ) : (
          dataKeys.length > 0 && (
            <ErrorDisplay>
              {dataKeys.map(key => (
                <Fragment key={key}>
                  {key}: {data[key]}
                  <br />
                </Fragment>
              ))}
            </ErrorDisplay>
          )
        )}
      </Container>
    )
  }

  return (
    <Container>
      <ErrorTitle status={500} statusText={strings.unexpectedError} />
      <Link
        to={ROUTES.CHOOSE_SERVICE.$path()}
        className={classNames(focusRing, 'underline')}
      >
        {strings.homepage}
      </Link>
      {error instanceof Error ? (
        <Disclosure>
          {({ open }) => (
            <ErrorDisplay>
              {error.message || 'No error message'}
              <DisclosurePanel className='max-w-full'>
                <pre className='max-w-full break-all whitespace-pre-wrap'>
                  {error.stack}
                </pre>
              </DisclosurePanel>
              <DisclosureButton
                className={classNames(
                  focusRing,
                  'text-caption2 underline hover:no-underline',
                )}
              >
                {open ? 'Hide full stack' : 'Show full stack'}
              </DisclosureButton>
            </ErrorDisplay>
          )}
        </Disclosure>
      ) : (
        <ErrorDisplay>{JSON.stringify(error, null, 2)}</ErrorDisplay>
      )}
    </Container>
  )
}
